<template>
  <div class="sso-callback-container d-flex flex-column align-center justify-center fill-height">
    <v-progress-circular
      v-if="loading"
      indeterminate
      size="64"
      color="primary"></v-progress-circular>

    <div v-if="loading" class="mt-4 text-h6">
      Completing sign in...
    </div>

    <v-alert
      v-if="error"
      type="error"
      prominent
      class="mt-4"
      max-width="500">
      <div class="text-h6">Sign in failed</div>
      <div>{{ errorMessage }}</div>
      <v-btn
        class="mt-4"
        color="white"
        outlined
        @click="goToLogin">
        Return to Login
      </v-btn>
    </v-alert>
  </div>
</template>

<script>
import { $setToLocal } from '@/utils';
import { log, error as logError } from '@/utils/debugLogger';
import accountService from '@/features/Account/service/account.service';
import { takeRememberedReturnPath } from '@/utils/returnPath';

export default {
  name: 'SSOCallback',
  data() {
    return {
      loading: true,
      error: false,
      errorMessage: '',
    };
  },
  methods: {
    async processCallback() {
      try {
        const urlParams = new URLSearchParams(window.location.search);

        // Check for error in query params
        const errorParam = urlParams.get('error');
        if (errorParam) {
          this.error = true;
          this.errorMessage = urlParams.get('error_description') || errorParam;
          this.loading = false;
          return;
        }

        // Check for token in query params
        const accessToken = urlParams.get('access_token');
        const refreshToken = urlParams.get('refresh_token');

        if (accessToken && refreshToken) {
          const expiresIn = urlParams.get('expires_in');
          const isNewUser = urlParams.get('is_new_user') === 'True';

          // Store tokens first so httpService can use them for the self() call
          $setToLocal('accessToken', accessToken);
          $setToLocal('refreshToken', refreshToken);
          if (expiresIn) {
            $setToLocal('tokenExpiresIn', expiresIn);
          }
          $setToLocal('user',{},true);
          log('SSO login successful, isNewUser:', isNewUser);

          // Fetch user data using account service
          const userResponse = await accountService.self();
          const user = userResponse.data;

          // Set user in Vuex store with all required parameters
          this.$store.commit('ACCOUNT/SET_USER', {
            user,
            accessToken,
            refreshToken,
            accessTokenExpiresAt: null,
            expiresIn: expiresIn ? parseInt(expiresIn, 10) : null,
            isImpersonateMode: false,
            isMultiAccountMode: false,
          });

          // Back to where sign-in started (kept by SSOLoginButton), else home.
          this.$router.replace(takeRememberedReturnPath() || '/');
          return;
        }

        // No token found - redirect to login with error
        this.error = true;
        this.errorMessage = 'No authentication data received. Please try again.';
        this.loading = false;
      } catch (err) {
        logError('SSO callback error:', err);
        this.error = true;
        this.errorMessage = err.message || 'An unexpected error occurred during sign in.';
        this.loading = false;
      }
    },
    goToLogin() {
      this.$router.replace('/login');
    },
  },
  mounted() {
    this.processCallback();
  },
};
</script>

<style scoped>
.sso-callback-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}
</style>
